"use client"

import type React from "react"
import { useEffect, useRef } from "react"
import { mouseStore } from "../mouseStore"

interface CursorDrawProps {
  /** Ink color */
  color?: string
  /** Stroke width in px */
  width?: number
  /** z-index */
  zIndex?: number
  /**
   * How long strokes persist in ms before fading out.
   * 0 = permanent until double-click or unmount.
   */
  fadeTime?: number
  /** When false, drawing is paused but existing strokes are preserved */
  enabled?: boolean
  /** Eraser diameter in px (right-click drag to erase) */
  eraseWidth?: number
  /** Called after every draw stroke is completed */
  onStroke?: (points: { x: number; y: number }[]) => void
}

interface Stroke {
  /** Points in page (document) coordinates — scroll-anchored */
  points: { x: number; y: number }[]
  color: string
  width: number
  createdAt: number
  /** When true, renders with destination-out to erase underlying pixels */
  erase: boolean
}

export const CursorDraw: React.FC<CursorDrawProps> = ({
  color = "rgba(255,255,255,0.85)",
  width = 2,
  zIndex = 9996,
  fadeTime = 0,
  enabled = true,
  eraseWidth = 20,
  onStroke,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const strokesRef = useRef<Stroke[]>([])
  const currentRef = useRef<{ x: number; y: number }[] | null>(null)
  const isDownRef = useRef(false)
  const isErasingRef = useRef(false)
  const rafRef = useRef<number | null>(null)
  const enabledRef = useRef(enabled)

  useEffect(() => {
    enabledRef.current = enabled
    if (!enabled) {
      isDownRef.current = false
      if (currentRef.current && currentRef.current.length > 1) {
        strokesRef.current.push({
          points: currentRef.current,
          color,
          width,
          createdAt: performance.now(),
          erase: false,
        })
      }
      currentRef.current = null
    }
  }, [enabled, color, width])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const resize = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resize()
    window.addEventListener("resize", resize, { passive: true })

    const drawStroke = (pts: { x: number; y: number }[], sw: number, sc: string, alpha: number) => {
      if (pts.length < 2) return
      ctx.beginPath()
      ctx.moveTo(pts[0].x, pts[0].y)
      for (let i = 1; i < pts.length - 1; i++) {
        const mx = (pts[i].x + pts[i + 1].x) / 2
        const my = (pts[i].y + pts[i + 1].y) / 2
        ctx.quadraticCurveTo(pts[i].x, pts[i].y, mx, my)
      }
      ctx.lineTo(pts[pts.length - 1].x, pts[pts.length - 1].y)
      ctx.strokeStyle = sc
      ctx.globalAlpha = alpha
      ctx.lineWidth = sw
      ctx.lineCap = "round"
      ctx.lineJoin = "round"
      ctx.stroke()
    }

    const animate = (time: number) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // Translate to page coordinates so drawings scroll with content
      const sx = window.scrollX
      const sy = window.scrollY
      ctx.save()
      ctx.translate(-sx, -sy)

      if (fadeTime > 0) {
        strokesRef.current = strokesRef.current.filter((s) => time - s.createdAt < fadeTime + 800)
      }

      strokesRef.current.forEach((s) => {
        const age = fadeTime > 0 ? Math.max(0, time - s.createdAt - fadeTime) : 0
        const alpha = fadeTime > 0 ? Math.max(0, 1 - age / 800) : 1

        if (s.erase) {
          ctx.globalCompositeOperation = "destination-out"
          drawStroke(s.points, s.width, "rgba(0,0,0,1)", alpha || 1)
          ctx.globalCompositeOperation = "source-over"
        } else {
          drawStroke(s.points, s.width, s.color, alpha)
        }
      })

      if (currentRef.current && currentRef.current.length > 0) {
        if (isErasingRef.current) {
          // Active erase stroke
          ctx.globalCompositeOperation = "destination-out"
          drawStroke(currentRef.current, eraseWidth, "rgba(0,0,0,1)", 1)
          ctx.globalCompositeOperation = "source-over"

          // Eraser circle indicator
          const tip = currentRef.current[currentRef.current.length - 1]
          ctx.beginPath()
          ctx.arc(tip.x, tip.y, eraseWidth / 2, 0, Math.PI * 2)
          ctx.setLineDash([3, 3])
          ctx.strokeStyle = "rgba(255,255,255,0.5)"
          ctx.lineWidth = 1
          ctx.globalAlpha = 0.7
          ctx.stroke()
          ctx.setLineDash([])
        } else {
          drawStroke(currentRef.current, width, color, 1)
        }
      }

      ctx.restore()
      ctx.globalAlpha = 1

      const isDrawing = currentRef.current !== null
      const hasFadingWork = fadeTime > 0 && strokesRef.current.length > 0

      if (isDrawing || hasFadingWork) {
        rafRef.current = requestAnimationFrame(animate)
      } else {
        rafRef.current = null
      }
    }

    // Scroll shifts the viewport — repaint strokes at their new screen position
    const onScroll = () => {
      const hasStrokes = strokesRef.current.length > 0 || currentRef.current !== null
      if (hasStrokes && rafRef.current === null) {
        rafRef.current = requestAnimationFrame(animate)
      }
    }

    const onDown = (e: MouseEvent) => {
      if (!enabledRef.current) return
      // Left button (0) = draw, right button (2) = erase
      if (e.button !== 0 && e.button !== 2) return
      isErasingRef.current = e.button === 2
      isDownRef.current = true
      // Suppress text selection while drawing or erasing
      document.body.style.userSelect = "none"
      ;(document.body.style as any).webkitUserSelect = "none"
      currentRef.current = [{ x: e.clientX + window.scrollX, y: e.clientY + window.scrollY }]
      if (rafRef.current === null) rafRef.current = requestAnimationFrame(animate)
    }

    const onUp = (e: MouseEvent) => {
      if (e.button !== 0 && e.button !== 2) return
      // Restore text selection
      document.body.style.userSelect = ""
      ;(document.body.style as any).webkitUserSelect = ""
      if (!isDownRef.current || !currentRef.current) return
      isDownRef.current = false
      if (currentRef.current.length > 1) {
        const stroke: Stroke = {
          points: currentRef.current,
          color,
          width: isErasingRef.current ? eraseWidth : width,
          createdAt: performance.now(),
          erase: isErasingRef.current,
        }
        strokesRef.current.push(stroke)
        if (!isErasingRef.current) onStroke?.(stroke.points)
        if (fadeTime > 0 && rafRef.current === null) rafRef.current = requestAnimationFrame(animate)
      }
      currentRef.current = null
      isErasingRef.current = false
    }

    // Double-click clears all strokes
    const onDblClick = () => {
      if (!enabledRef.current) return
      strokesRef.current = []
      currentRef.current = null
      isDownRef.current = false
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }

    // Prevent browser context menu while draw plugin is enabled
    const onContextMenu = (e: MouseEvent) => {
      if (enabledRef.current) e.preventDefault()
    }

    const unsubMove = mouseStore.subscribe((x, y) => {
      if (!enabledRef.current || !isDownRef.current || !currentRef.current) return
      currentRef.current.push({ x: x + window.scrollX, y: y + window.scrollY })
    })

    window.addEventListener("mousedown", onDown)
    window.addEventListener("mouseup", onUp)
    window.addEventListener("dblclick", onDblClick)
    window.addEventListener("contextmenu", onContextMenu)
    window.addEventListener("scroll", onScroll, { passive: true })

    return () => {
      window.removeEventListener("resize", resize)
      window.removeEventListener("mousedown", onDown)
      window.removeEventListener("mouseup", onUp)
      window.removeEventListener("dblclick", onDblClick)
      window.removeEventListener("contextmenu", onContextMenu)
      window.removeEventListener("scroll", onScroll)
      document.body.style.userSelect = ""
      ;(document.body.style as any).webkitUserSelect = ""
      unsubMove()
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    }
  }, [color, eraseWidth, fadeTime, width])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{ position: "fixed", top: 0, left: 0, pointerEvents: "none", zIndex }}
    />
  )
}
